import { BigNumber } from 'bignumber.js';
import { ethers } from 'ethers';

import { ERC20ABI } from 'structure/pv2/vault/components/abi/ERC20ABI';
import { StakingContract } from 'utils/constants/abi/StakingContract';
import rewardsContractAbi from 'utils/constants/abi/Rewards.json';
import { MulticallWrapper } from 'ethers-multicall-provider';

export interface APRCalculationInputs {
  userREP: string;
  totalReputationScoreOfPool: string;
  totalRewards: string;
  rewardsEmissionPeriod: string;
  rewardsCommissionPercentage: string;
  userAmountStaked: string;
}

export interface APRCalculationResult {
  apr: string;
  isValid: boolean;
  error?: string;
}

export function calculateAPR(
  inputs: APRCalculationInputs
): APRCalculationResult {
  try {
    const userREP = new BigNumber(inputs.userREP);
    const totalReputationScoreOfPool = new BigNumber(
      inputs.totalReputationScoreOfPool
    );
    const totalRewards = new BigNumber(inputs.totalRewards);
    const rewardsEmissionPeriod = new BigNumber(inputs.rewardsEmissionPeriod);
    const rewardsCommissionPercentage = new BigNumber(
      inputs.rewardsCommissionPercentage
    );
    const userAmountStaked = new BigNumber(inputs.userAmountStaked);
    const SECONDS_IN_YEAR = new BigNumber('31536000'); // 365 * 24 * 60 * 60

    if (userREP.isZero()) {
      return { apr: '0', isValid: true, error: 'User REP is zero' };
    }
    if (totalReputationScoreOfPool.isZero()) {
      return {
        apr: '0',
        isValid: true,
        error: 'Total Reputation Score of Pool is zero',
      };
    }
    if (totalRewards.isZero()) {
      return { apr: '0', isValid: true, error: 'Total Rewards is zero' };
    }
    if (rewardsEmissionPeriod.isZero()) {
      return {
        apr: '0',
        isValid: true,
        error: 'Pool has no rewards emission period configured',
      };
    }
    if (userAmountStaked.isZero()) {
      return { apr: '0', isValid: true, error: 'User Amount Staked is zero' };
    }

    const rewardsAfterCommissionPerPeriod = totalRewards.times(
      new BigNumber(1).minus(rewardsCommissionPercentage.dividedBy(100))
    );

    const userShareOfRep = userREP.dividedBy(totalReputationScoreOfPool);

    const userRewardsPerPeriod =
      rewardsAfterCommissionPerPeriod.times(userShareOfRep);

    const periodsPerYear = SECONDS_IN_YEAR.dividedBy(rewardsEmissionPeriod);

    const aprPercent = userRewardsPerPeriod
      .times(periodsPerYear)
      .dividedBy(userAmountStaked)
      .times(100);

    return {
      apr: aprPercent.toString(),
      isValid: true,
    };
  } catch (error) {
    return {
      apr: '0',
      isValid: false,
      error:
        error instanceof Error
          ? error.message
          : 'Unknown error in APR calculation',
    };
  }
}

export function formatAPR(apr: string, decimals: number = 2): string {
  try {
    const aprNumber = parseFloat(apr);
    if (isNaN(aprNumber)) {
      return '0.00';
    }
    return aprNumber.toFixed(decimals);
  } catch {
    return '0.00';
  }
}

export function createAPRInputs(
  pool: {
    totalRewards: string;
    rewardsEmissionPeriod: string;
    rewardsCommissionPercentage: string;
    totalReputationScore: string;
  },
  userData: {
    reputationScore: string;
    totalStaked: string;
  }
): APRCalculationInputs {
  return {
    userREP: userData.reputationScore,
    totalReputationScoreOfPool: pool.totalReputationScore,
    totalRewards: pool.totalRewards,
    rewardsEmissionPeriod: pool.rewardsEmissionPeriod,
    rewardsCommissionPercentage: pool.rewardsCommissionPercentage,
    userAmountStaked: userData.totalStaked,
  };
}

export function calculateAverageAPR({
  rewardsEmissionPeriod,
  totalReward,
  tokensLocked,
  endOfEmission,
}: {
  rewardsEmissionPeriod: string;
  totalReward: string;
  tokensLocked: string;
  endOfEmission: string;
}): string | Error {
  try {
    const currentTime = Math.floor(Date.now() / 1000);
    const isEmissionStopped = +endOfEmission < currentTime;

    if (isEmissionStopped) {
      return '0';
    }

    const averageAPR = new BigNumber(totalReward)
      .div(rewardsEmissionPeriod)
      .times(365 * 24 * 60 * 60)
      .div(new BigNumber(tokensLocked))
      .times(100)
      .toFixed(2);

    return averageAPR;
  } catch (error) {
    return new Error(`Error calculating average APR ${error}`);
  }
}

export async function getStakingContractMetadata(
  contractAddress: string,
  multicallProvider: MulticallWrapper
) {
  try {
    const contractCallContext = [
      {
        functionName: 'MAX_EARLY_UNLOCK_FEE',
        args: [],
      },
      {
        functionName: 'MAX_EARLY_UNLOCK_FEE_PER_DAY',
        args: [],
      },
      {
        functionName: 'MAX_LOCKING_PERIOD',
        args: [],
      },
      {
        functionName: 'MAX_PERCENTAGE',
        args: [],
      },
      {
        functionName: 'claim',
        args: [],
      },
      {
        functionName: 'depositToken',
        args: [],
      },
      {
        functionName: 'earlyUnlockFeePerDay',
        args: [],
      },
      {
        functionName: 'earlyUnlockFees',
        args: [],
      },
      {
        functionName: 'owner',
        args: [],
      },
      {
        functionName: 'rewardToken',
        args: [],
      },
      {
        functionName: 'rewardsAPI',
        args: [],
      },
      {
        functionName: 'totalScore',
        args: [],
      },
    ];
    const contract = new ethers.Contract(
      contractAddress,
      StakingContract,
      multicallProvider as unknown as ethers.providers.JsonRpcProvider
    );

    const result = await Promise.all(
      contractCallContext.map(async (call) => {
        return contract.callStatic[call.functionName](...call.args);
      })
    );

    const poolMetadata: {
      MAX_EARLY_UNLOCK_FEE_PER_DAY: string;
      MAX_EARLY_UNLOCK_FEE: string;
      MAX_LOCKING_PERIOD: string;
      MAX_PERCENTAGE: string;
      contractAddress: string;
      claim: string;
      depositTokenAddress: string;
      earlyUnlockFeePerDay: string;
      earlyUnlockFees: string;
      ownerAddress: string;
      rewardTokenAddress: string;
      rewardsContractAddress: string;
      totalScore: string;
    } = {
      MAX_EARLY_UNLOCK_FEE_PER_DAY: '',
      MAX_EARLY_UNLOCK_FEE: '',
      MAX_LOCKING_PERIOD: '',
      MAX_PERCENTAGE: '',
      contractAddress: contractAddress,
      claim: '',
      depositTokenAddress: '',
      earlyUnlockFeePerDay: '',
      earlyUnlockFees: '',
      ownerAddress: '',
      rewardTokenAddress: '',
      rewardsContractAddress: '',
      totalScore: '',
    };
    result.forEach((result, index) => {
      switch (contractCallContext[index].functionName) {
        case 'MAX_EARLY_UNLOCK_FEE':
          poolMetadata.MAX_EARLY_UNLOCK_FEE = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'MAX_EARLY_UNLOCK_FEE_PER_DAY':
          poolMetadata.MAX_EARLY_UNLOCK_FEE_PER_DAY = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'MAX_LOCKING_PERIOD':
          poolMetadata.MAX_LOCKING_PERIOD = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'MAX_PERCENTAGE':
          poolMetadata.MAX_PERCENTAGE = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'claim':
          poolMetadata.claim = result.toString();
          break;
        case 'depositToken':
          poolMetadata.depositTokenAddress = result.toString();
          break;
        case 'earlyUnlockFeePerDay':
          poolMetadata.earlyUnlockFeePerDay = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'earlyUnlockFees':
          poolMetadata.earlyUnlockFees = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        case 'owner':
          poolMetadata.ownerAddress = result.toString();
          break;
        case 'rewardToken':
          poolMetadata.rewardTokenAddress = result.toString();
          break;
        case 'rewardsAPI':
          poolMetadata.rewardsContractAddress = result.toString();
          break;
        case 'totalScore':
          poolMetadata.totalScore = ethers.BigNumber.from(
            result.toString()
          ).toString();
          break;
        default:
          break;
      }
    });
    return poolMetadata;
  } catch (error) {
    console.error(error);
    return new Error(`Error getting staking contract metadata ${error}`);
  }
}

export async function getTotalVolumeLocked(
  depositTokenAddress: string,
  contractAddress: string,
  provider: ethers.providers.JsonRpcProvider
) {
  try {
    const tokenContract = new ethers.Contract(
      depositTokenAddress,
      ERC20ABI,
      provider
    );
    const totalVolumeLocked = await tokenContract.balanceOf(contractAddress);
    return ethers.BigNumber.from(totalVolumeLocked).toString();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getTotalRewards(
  rewardsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider
) {
  try {
    const rewardsContract = new ethers.Contract(
      rewardsContractAddress,
      rewardsContractAbi,
      provider
    );
    const reward = await rewardsContract.reward();
    const totalRewards = reward.totalAmount;
    return totalRewards.toString();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getRewardsContractMetadata(
  contractAddress: string,
  multicallProvider: MulticallWrapper
) {
  try {
    const contractCallContext = [
      {
        address: contractAddress,
        abi: rewardsContractAbi,
        functionName: 'getRewardInfo',
        args: [],
      },
    ];
    const rewardsMetadata: {
      contractAddress: string;
      emissionPeriod: string;
      endOfEmission: string;
    } = {
      contractAddress: contractAddress,
      emissionPeriod: '',
      endOfEmission: '',
    };
    const contract = new ethers.Contract(
      contractAddress,
      rewardsContractAbi,
      multicallProvider as unknown as ethers.providers.JsonRpcProvider
    );
    const result = await Promise.all(
      contractCallContext.map(async (call) => {
        return contract.callStatic[call.functionName](...call.args);
      })
    );
    rewardsMetadata.emissionPeriod = result[0]?.[3]?.toString();
    rewardsMetadata.endOfEmission = result[0]?.[5]?.toString();

    return rewardsMetadata;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
